import classNames from 'classnames';
import type { ReactNode, VoidFunctionComponent } from 'react';

import Badge from '../../../generic/Badge/Badge';
import { BUTTON_SIZE, BUTTON_TYPE } from '../../../generic/Button/button-constants';
import NavigationButton from '../../../generic/Button/NavigationButton';
import TooltipButton from '../../../generic/Button/TooltipButton';
import InlineCopiableText from '../../../generic/CopiableText/InlineCopiableText';
import { DetailsPanelLink } from '../../../generic/DetailsPanel';
import { Icon, icons, IconText } from '../../../generic/Icon';
import InfoPanel from '../../../generic/InfoPanel';
import Link from '../../../generic/Link/Link';
import type { FossabotPRStatus } from '../../../../../shared/fossabot/common';
import type { IconClassName } from '../../../generic/Icon';

import { UPGRADE_BADGE } from './upgradeBadges';
import { useFossabotFixDisplay } from './useFossabotFixDisplay';

import styles from './FossabotFixBox.module.less';

/**
 * The box's own status: every PR status, plus two UI-only states that are not
 * part of the API type: 'error' for when the status could not be read at all,
 * and 'analysisDelayed' for 'analyzing' with the isAnalysisDelayed flag set.
 */
export type FossabotFixBoxStatus = FossabotPRStatus | 'error' | 'analysisDelayed';

const SUPPORTED_ECOSYSTEMS_URL = 'https://docs.fossa.com/docs/fossabot/supported-ecosystems';

/** Why fossabot cannot open a PR for this dependency at all, shown in place of the create button. */
export type FossabotBlockedNotice = {
  heading: string;
  description: string;
  /** Adds the link to the supported ecosystems docs under the description. */
  hasEcosystemsLink?: boolean;
};

type Props = {
  status: FossabotFixBoxStatus;
  /** PR URL in the VCS (GitHub). Null until the PR exists. */
  vcsUrl: string | null;
  /** PR URL in the fossabot app. Null until it exists. */
  fossabotUrl: string | null;
  /** Create / Open new PR buttons, and the failed-state Try again button, call this with the version shown on the card. */
  onCreatePR: (version: string) => void;
  /** Shown as the description of the 'error' and 'failed' states; each falls back to generic copy. */
  errorMessage?: string;
  /**
   * The 'error' and 'analysisDelayed' states' Try again button calls this
   * instead of onCreatePR (refetch the status, or re-run the analysis).
   */
  onRetry?: () => void;
  /**
   * When set, the create button is disabled and this text explains why in a tooltip.
   * Only affects 'none' and 'merged', the two states that open a brand new PR;
   * the retry states already have a PR, so their button stays live.
   */
  disabledReason?: string;
  /**
   * When set, no new PR can be opened for this dependency: the create button
   * gives way to a notice explaining why. Like disabledReason, it only affects
   * 'none' and 'merged'.
   */
  blockedNotice?: FossabotBlockedNotice;
};

type ChipConfig = {
  label: string;
  /** Resolved CSS-module class controlling the chip's fill/border colour. */
  modifierClass?: string;
  icon?: IconClassName;
  hasSpin?: boolean;
  /** Resolved CSS-module class controlling the icon's colour. */
  iconClass?: string;
};

/*
 * The status chip is hand-rolled rather than built on the shared Badge: it sits
 * on the black header and needs white text/border with an independently coloured
 * icon, which Badge applies as a single colour to text, border, and icon at once
 * (the sibling FossabotConnectionBanner hand-rolls its chip for the same reason).
 */
const CHIP_CONFIG: Record<FossabotFixBoxStatus, ChipConfig> = {
  none: { label: 'Recommended Fix', modifierClass: styles.chipRecommended },
  creating: { label: 'Creating PR', icon: icons.spinner, hasSpin: true, iconClass: styles.iconGreen },
  analyzing: { label: 'Analyzing', icon: icons.spinner, hasSpin: true, iconClass: styles.iconGreen },
  analysisDelayed: { label: 'Analyzing', icon: icons.spinner, hasSpin: true, iconClass: styles.iconGreen },
  analysisNotRun: { label: 'Analysis not run', icon: icons.code, iconClass: styles.iconYellow },
  needsReview: { label: 'Needs review', icon: icons.code, iconClass: styles.iconYellow },
  readyToMerge: { label: 'Ready to merge', icon: icons.check, iconClass: styles.iconGreen },
  merged: { label: 'Merged', icon: icons.check, iconClass: styles.iconGreen },
  closed: { label: 'Closed' },
  failed: { label: 'Failed', modifierClass: styles.chipFailed },
  // No iconClass: the icon inherits the red text colour chipFailed sets.
  error: { label: 'Error', modifierClass: styles.chipFailed, icon: icons.xMark },
};

/** Statuses where the PR is still open, so GitHub is where it gets reviewed. */
const OPEN_PR_STATUSES = new Set<FossabotPRStatus>(['analyzing', 'analysisNotRun', 'needsReview', 'readyToMerge']);

/** The GitHub button label, when one is shown. 'merged' has none: its action is Open new PR. */
const getGitHubButtonLabel = (status: FossabotFixBoxStatus): string | null => {
  if (status === 'closed') {
    return 'View on GitHub';
  }
  if (OPEN_PR_STATUSES.has(status)) {
    return 'Review on GitHub';
  }
  return null;
};

/** The primary button label, when one is shown. */
const getCreateButtonLabel = (status: FossabotFixBoxStatus): string | null => {
  if (status === 'none') {
    return 'Create PR';
  }
  if (status === 'failed' || status === 'error' || status === 'analysisDelayed') {
    return 'Try Again';
  }
  /*
    A merged PR can leave the issue open (the merge was reverted, or the upgrade
    did not clear the vulnerability), so the user needs a way to ask for another
    one. The create route already handles fossabot answering that a PR exists.
  */
  if (status === 'merged') {
    return 'Open New PR';
  }
  return null;
};

const FossabotStatusChip: VoidFunctionComponent<{ status: FossabotFixBoxStatus }> = ({ status }) => {
  const { label, modifierClass, icon, hasSpin, iconClass } = CHIP_CONFIG[status];
  return (
    <span className={classNames(styles.chip, modifierClass)}>
      {icon && (
        <span className={classNames(styles.chipIcon, iconClass)}>
          <Icon icon={icon} hasSpin={hasSpin} />
        </span>
      )}
      {label}
    </span>
  );
};

/**
 * The "Fix with fossabot" card shown in the vulnerability drawer's Remediation
 * section: a black-header card that renders all FossabotPRStatus states.
 *
 * Presentational only. The PR status and the two PR URLs arrive as props and the
 * Create action is a callback; the box makes no API or mutation calls. The fix
 * display data (version, upgrade size, fix type, CVE) is read from the active
 * VulnIssue in Redux via useFossabotFixDisplay.
 */
export const FossabotFixBox: VoidFunctionComponent<Props> = ({
  status,
  vcsUrl,
  fossabotUrl,
  onCreatePR,
  errorMessage,
  onRetry,
  disabledReason,
  blockedNotice,
}) => {
  const { version, distance, isComplete, cve, vulnCount } = useFossabotFixDisplay();

  const gitHubLabel = getGitHubButtonLabel(status);
  const createLabel = getCreateButtonLabel(status);
  const isNewPrStatus = status === 'none' || status === 'merged';
  const isCreateDisabled = disabledReason !== undefined && isNewPrStatus;
  const notice = isNewPrStatus ? blockedNotice : undefined;
  // The retry states already have (or are still reading) a PR; Try again never creates one.
  const onPrimaryClick = status === 'error' || status === 'analysisDelayed' ? onRetry : () => onCreatePR(version);
  // 'merged' drops the GitHub button but keeps the field, which opens GitHub on click.
  const shouldShowPrLink = gitHubLabel !== null || status === 'merged';
  const shouldShowFossabotLink = status !== 'none';
  const upgradeBadge = distance ? UPGRADE_BADGE[distance] : null;

  const renderDescription = (): ReactNode => {
    switch (status) {
      case 'none':
        return isComplete ? (
          <>
            Fixes all{' '}
            {vulnCount !== null && (
              <>
                <strong>{vulnCount}</strong>{' '}
              </>
            )}
            known vulnerabilities on this dependency.
          </>
        ) : (
          <>
            Fixes{' '}
            <DetailsPanelLink to={`https://nvd.nist.gov/vuln/detail/${cve}`}>{cve}</DetailsPanelLink>
            {' '}only. Other known vulnerabilities on this dependency remain.
          </>
        );
      case 'creating':
        return 'Opening a pull request on your repository...';
      case 'analyzing':
        return <><strong>fossabot</strong> is analyzing...</>;
      case 'analysisDelayed':
        return 'Analysis is taking longer than expected. Check the fossabot app for live status, or retry the scan.';
      case 'analysisNotRun':
        return <><strong>fossabot</strong> analysis was not run.</>;
      case 'needsReview':
      case 'readyToMerge':
        return <><strong>fossabot</strong> analysis complete!</>;
      case 'merged':
        return 'This pull request has been merged.';
      case 'closed':
        return 'This pull request was closed without merging.';
      case 'failed':
        return errorMessage ?? <><strong>fossabot</strong>{' couldn\'t create this pull request.'}</>;
      case 'error':
        return errorMessage ?? 'Could not check the pull request status.';
      default:
        return null;
    }
  };

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span className={styles.brand}>
          <img className={styles.logo} src="/img/fossabot-logo-color.svg" alt="fossabot" />
          <span className={styles.title}>Fix with fossabot</span>
        </span>
        <FossabotStatusChip status={status} />
      </div>

      <div className={styles.body}>
        <div className={styles.topRow}>
          <span className={styles.upgradeHeading}>Upgrade to {version}</span>
          <span className={styles.badges}>
            {upgradeBadge && (
              <Badge color="white" icon={upgradeBadge.icon}>
                {upgradeBadge.label}
              </Badge>
            )}
            <Badge color={isComplete ? 'dark-green' : 'light-green'}>
              {isComplete ? 'Complete Fix' : 'Partial Fix'}
            </Badge>
          </span>
        </div>

        {/* On 'none' the description only sells the PR the notice says cannot be opened. */}
        {!(notice && status === 'none') && <p className={styles.description}>{renderDescription()}</p>}

        {shouldShowPrLink && vcsUrl && (
          <div className={styles.prLink}>
            <InlineCopiableText label="PR LINK" value={vcsUrl} openLinkOnClick />
          </div>
        )}

        {/* aria-live announces the notice to assistive tech: it can mount after the connection check resolves, with nothing else to draw focus. */}
        {notice && (
          <div aria-live="polite">
            <InfoPanel type="warning" icon={icons.triangleExclamationRegular}>
              <strong className={styles.noticeHeading}>{notice.heading}</strong>
              <p className={styles.noticeDescription}>{notice.description}</p>
              {notice.hasEcosystemsLink && (
                <Link className={styles.noticeLink} to={SUPPORTED_ECOSYSTEMS_URL} openInNewTab>
                  Learn about our supported ecosystems.
                </Link>
              )}
            </InfoPanel>
          </div>
        )}

        <div className={styles.actions}>
          {!notice && createLabel && onPrimaryClick && (
            <TooltipButton
              id="fossabot-create-pr-tooltip"
              tooltip={disabledReason}
              showTooltip={isCreateDisabled}
              isDisabled={isCreateDisabled}
              type={BUTTON_TYPE.SECONDARY}
              size={BUTTON_SIZE.MEDIUM}
              onClick={onPrimaryClick}
            >
              {createLabel}
            </TooltipButton>
          )}
          {gitHubLabel && vcsUrl && (
            <NavigationButton to={vcsUrl} type={BUTTON_TYPE.SECONDARY} size={BUTTON_SIZE.MEDIUM} openInNewTab>
              {gitHubLabel}
            </NavigationButton>
          )}
          {shouldShowFossabotLink && fossabotUrl && (
            <Link to={fossabotUrl} variant="primary" openInNewTab>
              <IconText text="View in the fossabot app" icon={icons.chevronRight} alignIcon="right" />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};
